/**
 * components/ui/primitives.tsx
 * ------------------------------------------------------------------
 * Shared building blocks. The Acepsle rule: SHARP buttons, ROUNDED
 * everything-else, on solid warm surfaces with soft shadows.
 * ------------------------------------------------------------------
 */
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dark';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand text-white hover:bg-brandHover shadow-card',
  secondary:
    'bg-white text-primary border border-black/10 hover:bg-gray-50 dark:bg-white/5 dark:text-gray-100 dark:border-white/10 dark:hover:bg-white/10',
  ghost:
    'bg-transparent text-primaryLight hover:bg-black/5 dark:text-gray-300 dark:hover:bg-white/5',
  danger: 'bg-red-600 text-white hover:bg-red-700 shadow-card',
  dark: 'bg-primary text-white hover:bg-primaryDark shadow-card',
};

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
  icon?: ReactNode;
}

/** Buttons are intentionally SHARP (rounded-none) — Acepsle's signature. */
export function Button({
  variant = 'primary',
  loading = false,
  icon,
  children,
  className = '',
  disabled,
  ...rest
}: BtnProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-none px-4 py-2.5 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-brand/30 disabled:cursor-not-allowed disabled:opacity-50 ${VARIANTS[variant]} ${className}`}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : icon}
      {children}
    </button>
  );
}

/**
 * Rounded solid card with a soft shadow. `accent` adds an orange left
 * rail; `hover` lifts the shadow on hover.
 */
export function Card({
  children,
  className = '',
  accent = false,
  hover = false,
}: {
  children: ReactNode;
  className?: string;
  accent?: boolean;
  hover?: boolean;
}) {
  return (
    <div
      className={`surface ${
        accent ? 'rounded-l-none rounded-r-card border-l-4 border-l-brand' : 'rounded-card'
      } p-5 ${hover ? 'transition-shadow hover:shadow-cardHover' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
          {hint}
        </span>
      )}
    </label>
  );
}

/* Rounded, chunky, solid fields. */
/* Modern LKC-style fields: chunky border-2, soft thick focus ring. */
const FIELD =
  'w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2.5 text-sm text-ink transition-all placeholder:text-gray-400 hover:border-gray-300 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/15 disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-gray-100 dark:hover:border-white/20';

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  const { className = '', ...rest } = props;
  return <input className={`${FIELD} ${className}`} {...rest} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { className = '', ...rest } = props;
  return <textarea className={`${FIELD} ${className}`} {...rest} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <select className={`${FIELD} ${className}`} {...rest}>
      {children}
    </select>
  );
}

/* Friendly rounded-full pill badges. */
export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'brand';
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-gray-100 text-gray-700 dark:bg-white/5 dark:text-gray-200',
    success: 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-300',
    warning: 'bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300',
    danger: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
    brand: 'bg-brandSoft text-brand',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-primary dark:text-white">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1 text-sm text-primaryLight dark:text-gray-400">
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12">
      <div className="relative h-12 w-12">
        <div
          className="absolute inset-0 animate-spin rounded-full"
          style={{
            border: '3px solid transparent',
            borderTopColor: '#FF6B35',
            borderRightColor: 'rgba(34,39,46,0.35)',
          }}
        />
      </div>
      {label && (
        <p className="animate-pulse text-sm text-gray-500 dark:text-gray-400">
          {label}
        </p>
      )}
    </div>
  );
}
